import React, { useMemo, useState } from 'react';

// Simple responsive SVG donut chart
// Props:
// - data: Array<{ label: string, value: number, color?: string }>
// - height?: number (default 220)
// - donut?: boolean (default true)
export default function SimplePieChart({ data = [], height = 220, donut = true }) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const { total, slices } = useMemo(() => {
    const total = Math.max(1, data.reduce((acc, d) => acc + (Number(d.value) || 0), 0));
    const slices = data.map((d) => ({ ...d, value: Number(d.value) || 0 }));
    return { total, slices };
  }, [data]);

  const fmt = (n) => new Intl.NumberFormat('es-CL').format(n || 0);
  const radius = Math.max(40, height / 2 - 24);
  const cx = height / 2;
  const cy = height / 2;
  const innerRadius = donut ? radius * 0.6 : 0;

  let startAngle = 0;

  return (
    <div className="w-full flex flex-col items-center">
      <svg width={height} height={height} role="img" aria-label="Gráfico circular">
        {slices.map((d, i) => {
          const value = Number(d.value) || 0;
          const angle = (value / total) * 2 * Math.PI;
          const endAngle = startAngle + angle;
          const largeArc = angle > Math.PI ? 1 : 0;

          const x1 = cx + radius * Math.cos(startAngle);
          const y1 = cy + radius * Math.sin(startAngle);
          const x2 = cx + radius * Math.cos(endAngle);
          const y2 = cy + radius * Math.sin(endAngle);

          const ix1 = cx + innerRadius * Math.cos(startAngle);
          const iy1 = cy + innerRadius * Math.sin(startAngle);
          const ix2 = cx + innerRadius * Math.cos(endAngle);
          const iy2 = cy + innerRadius * Math.sin(endAngle);

          const path = [
            `M ${ix1} ${iy1}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
            `L ${ix2} ${iy2}`,
            `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1}`,
            'Z'
          ].join(' ');

          const midAngle = startAngle + angle / 2;
          const labelRadius = radius * 1.15;
          const lx = cx + labelRadius * Math.cos(midAngle);
          const ly = cy + labelRadius * Math.sin(midAngle);

          const isHover = hoverIndex === i;
          startAngle = endAngle;

          return (
            <g
              key={i}
              onMouseEnter={() => setHoverIndex(i)}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <path
                d={path}
                fill={d.color || '#ec4899'}
                opacity={isHover ? 0.95 : 0.85}
                stroke="#fff"
                strokeWidth="2"
                transform={isHover ? `scale(1.03) translate(${cx * -0.03}, ${cy * -0.03})` : undefined}
              />
              {value > 0 && (
                <text x={lx} y={ly} textAnchor="middle" fontSize="10" fill="#374151">
                  {value}
                </text>
              )}
            </g>
          );
        })}
        {donut && (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="600" fill="#111827">
            {fmt(total)}
          </text>
        )}
      </svg>
      <div className="flex flex-wrap justify-center gap-3 mt-2">
        {slices.map((d, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-3 h-3 rounded-full" style={{ background: d.color || '#ec4899' }} />
            <span>{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
